import { Button } from "@nimbus-ds/components/Button";
import { useMutation, useQuery, type QueryKey } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ModalShell } from "./ModalShell";

// AccountPickerModal<T> — the one radio-list "bind an account/property" dialog
// the connector pickers share. GA4PropertyPickerModal and MetaAccountPickerModal
// were structural twins (the Meta modal's own docstring said "Mirrors
// GA4PropertyPickerModal — same shape"): identical query → preselect → radio
// list → select-mutation → refresh flow, differing only in the query key, the
// fetch/select functions, the preselect rule, the option projection, and the
// Spanish copy. All of that is now config; each picker is a ~15-line wrapper.
//
// Wrapped in ModalShell so the pickers GAIN Escape-to-close and the portal
// clip-safety the card modals already had (an intended improvement — see the
// modal-scaffolding consolidation). They keep their exact `lifecycleModal__*` /
// `ga4Picker__*` classNames, so the rendered surface is unchanged.

export type AccountPickerOption = {
  // The value the radio carries and `selectOption` receives.
  id: string;
  // Bold primary line.
  label: string;
  // Muted secondary line.
  meta: string;
  // Disabled radios (Meta's inactive accounts); omit/false = selectable.
  disabled?: boolean;
};

export type AccountPickerCopy = {
  title: string;
  loading: string;
  // Shown when the fetch errors.
  fetchError: string;
  // Shown when the fetch succeeds but returns no options.
  emptyError: string;
  // Shown when the select mutation errors.
  saveError: string;
  // Primary-button label (idle state).
  confirmLabel: string;
};

export type AccountPickerModalProps<TData> = {
  open: boolean;
  onClose: () => void;
  onSelected: () => void;
  // Heading id for aria-labelledby — kept per-picker for stable test hooks.
  labelledById: string;
  // The radio group `name` attribute.
  radioName: string;
  queryKey: QueryKey;
  fetchOptions: () => Promise<TData>;
  // Maps the fetched payload to the rendered options (in order).
  mapOptions: (data: TData) => AccountPickerOption[];
  // Picks the initially-selected id from the fetched payload ("" = none).
  preselect: (data: TData) => string;
  // Binds the chosen id server-side.
  selectOption: (id: string) => Promise<unknown>;
  copy: AccountPickerCopy;
};

export function AccountPickerModal<TData>({
  open,
  onClose,
  onSelected,
  labelledById,
  radioName,
  queryKey,
  fetchOptions,
  mapOptions,
  preselect,
  selectOption,
  copy,
}: AccountPickerModalProps<TData>) {
  const [selectedId, setSelectedId] = useState<string>("");

  const optionsQuery = useQuery({
    queryKey,
    queryFn: () => fetchOptions(),
    enabled: open,
  });

  // Pre-select per the picker's rule (re-connect binding, or first/first-active)
  // once, while nothing is chosen yet.
  useEffect(() => {
    if (!optionsQuery.data || selectedId) return;
    const preferred = preselect(optionsQuery.data);
    if (preferred) setSelectedId(preferred);
    // preselect is a stable per-wrapper closure; only re-run on new data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsQuery.data, selectedId]);

  const select = useMutation({
    mutationFn: () => selectOption(selectedId),
    onSuccess: () => {
      onSelected();
      onClose();
    },
  });

  if (!open) return null;

  const options = optionsQuery.data ? mapOptions(optionsQuery.data) : [];

  return (
    <ModalShell
      labelledById={labelledById}
      backdropClassName="lifecycleModal__backdrop"
      panelClassName="lifecycleModal"
      onClose={onClose}
    >
      <h4 id={labelledById}>{copy.title}</h4>
      <div className="ga4Picker__body">
        {optionsQuery.isLoading ? (
          <p className="subtle">{copy.loading}</p>
        ) : optionsQuery.isError ? (
          <p className="ga4Picker__error" role="alert">
            {copy.fetchError}
          </p>
        ) : options.length === 0 ? (
          <p className="ga4Picker__error" role="alert">
            {copy.emptyError}
          </p>
        ) : (
          <ul className="ga4Picker__list">
            {options.map((option) => (
              <li key={option.id}>
                <label className="ga4Picker__option">
                  <input
                    type="radio"
                    name={radioName}
                    value={option.id}
                    checked={selectedId === option.id}
                    disabled={option.disabled}
                    onChange={() => setSelectedId(option.id)}
                  />
                  <span className="ga4Picker__optionText">
                    <strong>{option.label}</strong>
                    <span className="ga4Picker__optionMeta">{option.meta}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {select.isError ? (
          <p className="ga4Picker__error" role="alert">
            {copy.saveError}
          </p>
        ) : null}
      </div>
      <div className="lifecycleModal__actions">
        <Button appearance="neutral" className="secondaryAction" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          appearance="primary"
          className="primaryAction"
          disabled={!selectedId || select.isPending}
          onClick={() => select.mutate()}
        >
          {select.isPending ? "Guardando…" : copy.confirmLabel}
        </Button>
      </div>
    </ModalShell>
  );
}
